import {
  Alert,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  Typography,
  FormControl,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableHead,
  TablePagination,
  Box,
  FormHelperText,
  Autocomplete,
  LinearProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { StyledSelect, StyledTextField } from "../../customComponents/styled/styledInputs";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { StyledMultiSelect } from "../../customComponents/styled/customMultiSelect";
import { useFormik } from "formik";
import * as yup from "yup";
import { useGetAllClassroomsQuery, useGetClassesPaginatedQuery } from "../classroom/classApiSlice";
import { useGetFeesQuery } from "../fees-schedule/feeScheduleApiSlice";
import {
  useGetSessionsPaginatedQuery,
} from "../settings/session/sessionApiSlice";
import {
  useCreateInvoiceMutation,
  useDeleteInvoiceMutation,
  useDeleteInvoicesMutation,
  useGetSchoolInvoicesQuery,
  useLazyPreviewInvoiceQuery,
  useNotifyInvoicesMutation,
} from "./invoicesApiSlice";
import toast from "../../../helpers/toast";
import InvoiceDialog from "./InvoiceDialog";
import InvoicePreviewDialog from "./InvoicePreviewDialog";
import ManageDiscountsDialog from "./ManageDiscountsDialog";
import ApplyDiscountDialog from "./ApplyDiscountDialog";
import NotifyParentsDialog from "./NotifyParentsDialog";

const validationSchema = yup.object({
  class: yup.string().required("Required"),
  targetClass: yup.string().required("Required"),
  sessionId: yup.string().required("Required"),
  termId: yup.string().required("Required"),
});

function Invoices() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openInvoice, setOpenInvoice] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterEvidence, setFilterEvidence] = useState(false);
  const [openManageDiscounts, setOpenManageDiscounts] = useState(false);
  const [openApplyDiscount, setOpenApplyDiscount] = useState(false);
  const [openNotifyParents, setOpenNotifyParents] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [pendingBody, setPendingBody] = useState(null);
  const [filterSession, setFilterSession] = useState(null);
  const [filterTermId, setFilterTermId] = useState("");
  const [filterClassroom, setFilterClassroom] = useState(null);
  const [filterSessionSearch, setFilterSessionSearch] = useState("");
  const [filterClassroomSearch, setFilterClassroomSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [justGeneratedIds, setJustGeneratedIds] = useState([]);

  const formik = useFormik({
    initialValues: {
      class: "",
      targetClass: "",
      sessionId: "",
      termId: "",
      items: [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const body = {
        classroom_id: values.targetClass,
        items: values.items.map((item) => item.value),
        term_id: values.termId,
        session_id: values.sessionId,
      };
      setPendingBody(body);
      triggerPreview({
        classroom_id: values.class,
        items: body.items,
        term_id: values.termId,
        session_id: values.sessionId,
      });
      setOpenPreview(true);
    },
  });

  const [classroomSearch, setClassroomSearch] = useState("");
  const { data: classroomsData } = useGetClassesPaginatedQuery({ page: 1, per_page: 10, q: classroomSearch });
  const classrooms = classroomsData?.classrooms ?? [];

  const [targetClassroomSearch, setTargetClassroomSearch] = useState("");
  const { data: targetClassroomsData } = useGetClassesPaginatedQuery({ page: 1, per_page: 10, q: targetClassroomSearch });
  const targetClassrooms = targetClassroomsData?.classrooms ?? [];

  const [genSessionSearch, setGenSessionSearch] = useState("");
  const { data: genSessionsData } = useGetSessionsPaginatedQuery({ page: 1, per_page: 10, q: genSessionSearch });
  const genSessions = genSessionsData?.sessions ?? [];
  const genSelectedSession = genSessions.find((s) => String(s.id) === String(formik.values.sessionId));
  const genTermOptions = genSelectedSession?.terms ?? [];
  const genSelectedTerm = genTermOptions.find((t) => String(t.id) === String(formik.values.termId));

  const { data: filterClassroomsData } = useGetClassesPaginatedQuery({ page: 1, per_page: 10, q: filterClassroomSearch });
  const filterClassrooms = filterClassroomsData?.classrooms ?? [];

  const { data: sessionsData } = useGetSessionsPaginatedQuery({ page: 1, per_page: 10, q: filterSessionSearch });
  const sessions = sessionsData?.sessions ?? [];
  const termOptions = filterSession?.terms ?? [];

  // Deep-link support so links from elsewhere (e.g. the payments dashboard's
  // "view invoices" drill-through) land pre-filtered instead of dumping the
  // user on an unfiltered list they have to re-filter by hand.
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: allClassroomsForDeepLink } = useGetAllClassroomsQuery(undefined, {
    skip: !searchParams.get("classroom_id"),
  });
  const { data: deepLinkSessionsData } = useGetSessionsPaginatedQuery(
    { page: 1, per_page: 100 },
    { skip: !searchParams.get("session_id") }
  );
  useEffect(() => {
    const classroomId = searchParams.get("classroom_id");
    const status = searchParams.get("status");
    const sessionId = searchParams.get("session_id");
    const termId = searchParams.get("term_id");
    if (!classroomId && !status && !sessionId) return;

    if (status) setFilterStatus(status);
    if (classroomId) {
      const match = allClassroomsForDeepLink?.find((c) => String(c.id) === String(classroomId));
      if (match) setFilterClassroom(match);
      else return; // wait for the list to load before clearing the URL
    }
    if (sessionId) {
      const match = deepLinkSessionsData?.sessions?.find((s) => String(s.id) === String(sessionId));
      if (match) {
        setFilterSession(match);
        if (termId) setFilterTermId(termId);
      } else return;
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allClassroomsForDeepLink, deepLinkSessionsData]);

  const feeClassroomParam = formik.values.targetClass
    ? `classroom/${formik.values.targetClass}${genSelectedTerm?.name ? `?term_name=${encodeURIComponent(genSelectedTerm.name)}` : ""}`
    : null;
  const { currentData: fees } = useGetFeesQuery(feeClassroomParam, {
    skip: !feeClassroomParam,
  });
  const feeOptions = useMemo(
    () =>
      (fees ?? []).map((fee) => ({
        label: fee.gender ? `${fee.name} (${fee.gender})` : fee.name,
        value: fee.id,
        is_compulsory: fee.is_compulsory,
      })),
    [fees]
  );

  const { currentData: schoolInvoicesData, isFetching: invoicesFetching } = useGetSchoolInvoicesQuery({
    status: filterStatus || undefined,
    has_evidence: filterEvidence || undefined,
    session_id: filterSession?.id || undefined,
    term_id: filterTermId || undefined,
    classroom_id: filterClassroom?.id || undefined,
    page: page + 1,
    per_page: rowsPerPage,
  });
  const schoolInvoices = schoolInvoicesData?.invoices ?? [];
  const totalInvoices = schoolInvoicesData?.pagination?.total_items ?? 0;

  const [generateFee, { isLoading: generateFeeLoading }] = useCreateInvoiceMutation();
  const [deleteInvoice] = useDeleteInvoiceMutation();
  const [deleteInvoices, { isLoading: bulkDeleteLoading }] = useDeleteInvoicesMutation();
  const [triggerPreview, { data: previewData, isFetching: previewLoading, error: previewError }] = useLazyPreviewInvoiceQuery();
  const [notifyInvoices, { isLoading: notifyLoading }] = useNotifyInvoicesMutation();

  useEffect(() => {
    if (!formik.values.targetClass || (formik.values.items?.length ?? 0) > 0) {
      return;
    }
    const compulsoryDefaults = feeOptions
      .filter((fee) => fee.is_compulsory)
      .map((fee) => ({ label: fee.label, value: fee.value }));
    if (compulsoryDefaults.length) {
      formik.setFieldValue("items", compulsoryDefaults);
    }
  }, [feeOptions, formik.values.targetClass, formik.values.items, formik.setFieldValue]);

  useEffect(() => {
    setSelectedInvoiceIds([]);
  }, [page, rowsPerPage, filterStatus, filterEvidence, filterSession?.id, filterTermId, filterClassroom?.id]);

  async function handleConfirmGenerate(selectedStudentIds) {
    if (!pendingBody) return;
    try {
      const response = await generateFee({ ...pendingBody, student_ids: selectedStudentIds });
      if (response.data) {
        toast.success(response.data.message);
        setOpenPreview(false);
        const newIds = response.data.invoice_ids || [];
        formik.resetForm();
        if (newIds.length) {
          setJustGeneratedIds(newIds);
        }
      } else {
        toast.error(response.error?.data?.message || "Invoice generation failed");
      }
    } catch (error) {
      toast.error("Invoice generation failed");
    }
  }

  async function handleNotifyInvoices(invoiceIds) {
    if (!invoiceIds?.length) return;
    try {
      const response = await notifyInvoices(invoiceIds);
      if (response.data) {
        toast.success(`Notified parents for ${response.data.notified} invoice(s)`);
        setJustGeneratedIds([]);
        setSelectedInvoiceIds([]);
      } else {
        toast.error(response.error?.data?.message || "Failed to notify parents");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  }

  async function handleDelete(id) {
    const confirm = window.confirm("Are you sure you want to delete this invoice?");
    if (!confirm) return;
    try {
      const response = await deleteInvoice(id);
      if (response.data) {
        setSelectedInvoiceIds((prev) => prev.filter((invoiceId) => invoiceId !== id));
        toast.success(response.data.message);
      } else {
        toast.error(response.error?.message || "Delete failed");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  }

  function toggleInvoiceSelection(invoiceId) {
    setSelectedInvoiceIds((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId]
    );
  }

  function handleToggleAll(checked) {
    if (!checked) {
      setSelectedInvoiceIds([]);
      return;
    }
    setSelectedInvoiceIds(schoolInvoices.map((invoice) => invoice.id));
  }

  async function handleDeleteSelected() {
    if (!selectedInvoiceIds.length) {
      toast.error("Select at least one invoice");
      return;
    }

    const confirm = window.confirm(`Are you sure you want to delete ${selectedInvoiceIds.length} selected invoice(s)?`);
    if (!confirm) return;

    try {
      const response = await deleteInvoices(selectedInvoiceIds);
      if (response.data) {
        const { deleted_count = 0, failed_count = 0 } = response.data.data || {};
        setSelectedInvoiceIds([]);
        if (failed_count > 0) {
          toast.info(`Deleted ${deleted_count} invoice(s). ${failed_count} could not be deleted.`);
        } else {
          toast.success(response.data.message || "Invoices deleted successfully");
        }
      } else {
        toast.error(response.error?.data?.message || "Bulk delete failed");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  }

  const allVisibleSelected =
    schoolInvoices.length > 0 &&
    schoolInvoices.every((invoice) => selectedInvoiceIds.includes(invoice.id));

  function multiHandleChange(selectedOption) {
    formik.setFieldValue("items", selectedOption);
  }

  function handleDownloadCsv() {
    if (!schoolInvoices?.length) {
      toast.error("No invoices to export");
      return;
    }

    const escapeCsv = (value) => {
      const str = String(value ?? "");
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const lines = [
      ["Student", "Class", "Term", "Session", "Status", "Amount", "Amount Paid", "Amount Due"].join(","),
      ...schoolInvoices.map((invoice) => {
        const totalAmount =
          invoice.total_amount ??
          invoice.items?.reduce((sum, item) => sum + (item.amount || 0), 0) ??
          0;
        const amountPaid = invoice.amount_paid || 0;
        const amountDue = Math.max(0, totalAmount - amountPaid);
        const status = invoice.paid
          ? "Paid"
          : invoice.partial_paid
            ? "Partially Paid"
            : "Unpaid";
        return [
          `${invoice.student?.first_name || ""} ${invoice.student?.last_name || ""}`.trim(),
          invoice.classroom?.name || "",
          invoice.term?.name || "",
          invoice.session?.name || "",
          status,
          totalAmount,
          amountPaid,
          amountDue,
        ]
          .map(escapeCsv)
          .join(",");
      }),
    ];

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `invoices_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function handlePrint() {
    if (!schoolInvoices?.length) {
      toast.error("No invoices to print");
      return;
    }

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const rowsHtml = schoolInvoices
      .map((invoice) => {
        const totalAmount =
          invoice.total_amount ??
          invoice.items?.reduce((sum, item) => sum + (item.amount || 0), 0) ??
          0;
        const amountPaid = invoice.amount_paid || 0;
        const amountDue = Math.max(0, totalAmount - amountPaid);
        const status = invoice.paid
          ? "Paid"
          : invoice.partial_paid
            ? "Partially Paid"
            : "Unpaid";
        const studentName =
          `${invoice.student?.first_name || ""} ${invoice.student?.last_name || ""}`.trim() || "-";
        return `
          <tr>
            <td>${escapeHtml(studentName)}</td>
            <td>${escapeHtml(invoice.classroom?.name || "-")}</td>
            <td>${escapeHtml(invoice.term?.name || "-")}</td>
            <td>${escapeHtml(invoice.session?.name || "-")}</td>
            <td>${escapeHtml(status)}</td>
            <td>&#8358;${Number(totalAmount).toLocaleString()}</td>
            <td>&#8358;${Number(amountPaid).toLocaleString()}</td>
            <td>&#8358;${Number(amountDue).toLocaleString()}</td>
          </tr>
        `;
      })
      .join("");

    const filterMeta = [
      filterSession ? `Session: ${filterSession.name}` : null,
      filterTermId ? `Term: ${termOptions.find((t) => t.id === Number(filterTermId))?.name || filterTermId}` : null,
      filterClassroom ? `Class: ${filterClassroom.name}` : null,
      filterStatus ? `Status: ${filterStatus}` : null,
    ]
      .filter(Boolean)
      .join(" | ");

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Invoices</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #111; }
            h1 { margin: 0 0 8px; font-size: 20px; }
            .meta { margin: 0 0 16px; color: #444; font-size: 13px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #cfcfcf; padding: 6px 8px; text-align: left; font-size: 12px; }
            th { background: #f3f3f3; }
            @media print { body { margin: 8mm; } }
          </style>
        </head>
        <body>
          <h1>Invoices</h1>
          <p class="meta">${escapeHtml(filterMeta || "All invoices")} | Generated: ${new Date().toLocaleString()} | Total: ${schoolInvoices.length}</p>
          <table>
            <thead>
              <tr>
                <th>Student</th><th>Class</th><th>Term</th><th>Session</th>
                <th>Status</th><th>Amount</th><th>Amount Paid</th><th>Amount Due</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open("", "Print Invoices");
    if (!printWindow) {
      toast.error("Unable to open print window");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }

  return (
    <div style={{ padding: "0 10px" }}>
      <InvoiceDialog
        open={openInvoice}
        setOpen={setOpenInvoice}
        selectedInvoice={selectedInvoice}
      />
      <InvoicePreviewDialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        previewData={previewData}
        previewLoading={previewLoading}
        previewError={previewError}
        onConfirm={handleConfirmGenerate}
        confirmLoading={generateFeeLoading}
        className={targetClassrooms.find((c) => c.id === formik.values.targetClass)?.name
          || classrooms.find((c) => c.id === formik.values.class)?.name}
      />
      {justGeneratedIds.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon />}
            sx={{
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 4px 12px rgba(0, 0, 0, 0.4)"
                  : "0 2px 8px rgba(0, 0, 0, 0.05)",
              "& .MuiAlert-message": {
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                width: "100%",
                justifyContent: "space-between",
              },
            }}
            action={
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setJustGeneratedIds([])}
                title="Dismiss"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, minWidth: 220 }}>
              {justGeneratedIds.length} invoice(s) generated. Parents have not been notified yet.
            </Typography>

            <LoadingButton
              variant="contained"
              loading={notifyLoading}
              size="small"
              color="info"
              onClick={() => handleNotifyInvoices(justGeneratedIds)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "12px",
                px: 2,
                py: 0.4,
                whiteSpace: "nowrap",
                boxShadow: "none",
              }}
            >
              Notify Parents Now
            </LoadingButton>
          </Alert>
        </Box>
      )}
      <ManageDiscountsDialog open={openManageDiscounts} setOpen={setOpenManageDiscounts} />
      <ApplyDiscountDialog open={openApplyDiscount} setOpen={setOpenApplyDiscount} />
      <NotifyParentsDialog open={openNotifyParents} setOpen={setOpenNotifyParents} />
      <Grid container columnSpacing={4}>
        <Grid item xs={12} md={4}>
          <StyledPaper sx={{ padding: "15px" }}>
            <Typography variant="formHeading" sx={{ marginBottom: "20px" }}>
              Generate Invoice
            </Typography>

            <Box component="form" onSubmit={formik.handleSubmit}>
              <Grid container rowSpacing={3}>
                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.class && formik.errors.class}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">CLASS (student list to invoice)</Typography>
                    <Autocomplete
                      options={classrooms}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={classrooms.find(c => c.id === formik.values.class) || null}
                      onChange={(_, value) => {
                        formik.setFieldValue("class", value?.id || "");
                        // Target class defaults to the same class, but can be
                        // changed independently — e.g. generating next term's
                        // invoices for the class these students are being
                        // promoted into.
                        if (!formik.values.targetClass) {
                          formik.setFieldValue("targetClass", value?.id || "");
                        }
                        formik.setFieldValue("items", []);
                      }}
                      onInputChange={(_, value, reason) => { if (reason === "input") setClassroomSearch(value); }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Search class..."
                          error={formik.touched.class && Boolean(formik.errors.class)}
                        />
                      )}
                    />
                    <FormHelperText variant="error">
                      {formik.touched.class && formik.errors.class}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.targetClass && formik.errors.targetClass}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">
                      INVOICE UNDER CLASS (fees &amp; term this invoice belongs to)
                    </Typography>
                    <Autocomplete
                      options={targetClassrooms}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={targetClassrooms.find(c => c.id === formik.values.targetClass) || classrooms.find(c => c.id === formik.values.targetClass) || null}
                      onChange={(_, value) => {
                        formik.setFieldValue("targetClass", value?.id || "");
                        formik.setFieldValue("items", []);
                      }}
                      onInputChange={(_, value, reason) => { if (reason === "input") setTargetClassroomSearch(value); }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Same as class above unless changed"
                          error={formik.touched.targetClass && Boolean(formik.errors.targetClass)}
                        />
                      )}
                    />
                    <FormHelperText variant="error">
                      {formik.touched.targetClass && formik.errors.targetClass}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.sessionId && formik.errors.sessionId}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">SESSION</Typography>
                    <Autocomplete
                      options={genSessions}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={genSessions.find((s) => s.id === formik.values.sessionId) || null}
                      onChange={(_, value) => {
                        formik.setFieldValue("sessionId", value?.id || "");
                        formik.setFieldValue("termId", "");
                      }}
                      onInputChange={(_, value, reason) => { if (reason === "input") setGenSessionSearch(value); }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Session"
                          error={formik.touched.sessionId && Boolean(formik.errors.sessionId)}
                        />
                      )}
                    />
                    <FormHelperText variant="error">
                      {formik.touched.sessionId && formik.errors.sessionId}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.termId && formik.errors.termId}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">TERM</Typography>
                    <StyledSelect
                      value={formik.values.termId}
                      onChange={(e) => {
                        formik.setFieldValue("termId", e.target.value);
                        formik.setFieldValue("items", []);
                      }}
                      displayEmpty
                      disabled={!formik.values.sessionId}
                      error={formik.touched.termId && Boolean(formik.errors.termId)}
                    >
                      <MenuItem value="" disabled>
                        <em>Select a term</em>
                      </MenuItem>
                      {genTermOptions.map((term) => (
                        <MenuItem key={term.id} value={term.id}>{term.name}</MenuItem>
                      ))}
                    </StyledSelect>
                    <FormHelperText variant="error">
                      {formik.touched.termId && formik.errors.termId}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.items && formik.errors.items}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">items</Typography>
                    <StyledMultiSelect
                      id="items"
                      name="items"
                      onChange={multiHandleChange}
                      value={formik.values.items}
                      error={
                        formik.touched.items && Boolean(formik.errors.items)
                      }
                      options={feeOptions.map((fee) => ({ label: fee.label, value: fee.value }))}
                      placeholder="Select Fees"
                    />

                    <FormHelperText variant="error">
                      {formik.touched.items && formik.errors.items}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12} textAlign={"right"}>
                  <StyledLoadingButton
                    loading={previewLoading}
                    type="submit"
                    sx={{ textTransform: "none" }}
                    onClick={formik.handleSubmit}
                  >
                    Preview
                  </StyledLoadingButton>
                </Grid>
              </Grid>
            </Box>
          </StyledPaper>
        </Grid>

        {/* Invoice Table */}

        <Grid item xs={12} md={8}>
          <StyledPaper sx={{ paddingBottom: "25px" }}>
            {/* Toolbar row 1: title + actions */}
            <Box sx={{ padding: "15px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
              <Typography variant="formHeading">Invoices</Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <StyledLoadingButton
                  variant="outlined"
                  sx={{ textTransform: "none" }}
                  onClick={() => setOpenManageDiscounts(true)}
                >
                  Manage Discounts
                </StyledLoadingButton>
                <StyledLoadingButton
                  sx={{ textTransform: "none" }}
                  onClick={() => setOpenApplyDiscount(true)}
                >
                  Apply Discount
                </StyledLoadingButton>
                <StyledLoadingButton
                  variant="outlined"
                  sx={{ textTransform: "none" }}
                  onClick={() => setOpenNotifyParents(true)}
                >
                  Notify Parents
                </StyledLoadingButton>
                <StyledLoadingButton
                  variant="outlined"
                  sx={{ textTransform: "none" }}
                  onClick={handleDownloadCsv}
                >
                  Download CSV
                </StyledLoadingButton>
                <StyledLoadingButton
                  variant="outlined"
                  sx={{ textTransform: "none" }}
                  onClick={handlePrint}
                >
                  Print
                </StyledLoadingButton>
              </Box>
            </Box>

            {/* Toolbar row 2: filters */}
            <Box sx={{ px: "15px", pb: "15px", display: "flex", gap: 2, alignItems: "flex-end", flexWrap: "wrap" }}>
              <Box sx={{ minWidth: 160 }}>
                <Typography variant="formSubHeading" sx={{ mb: 0.5 }}>SESSION</Typography>
                <Autocomplete
                  size="small"
                  options={sessions}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={filterSession}
                  onChange={(_, value) => {
                    setFilterSession(value);
                    setFilterTermId("");
                    setPage(0);
                  }}
                  onInputChange={(_, value, reason) => { if (reason === "input") setFilterSessionSearch(value); }}
                  filterOptions={(x) => x}
                  renderInput={(params) => <StyledTextField {...params} placeholder="All sessions" />}
                />
              </Box>

              <Box sx={{ minWidth: 140 }}>
                <Typography variant="formSubHeading" sx={{ mb: 0.5 }}>TERM</Typography>
                <FormControl size="small" fullWidth>
                  <StyledSelect
                    value={filterTermId}
                    onChange={(e) => { setFilterTermId(e.target.value); setPage(0); }}
                    displayEmpty
                    disabled={!filterSession}
                  >
                    <MenuItem value="">All Terms</MenuItem>
                    {termOptions.map((term) => (
                      <MenuItem key={term.id} value={term.id}>{term.name}</MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
              </Box>

              <Box sx={{ minWidth: 160 }}>
                <Typography variant="formSubHeading" sx={{ mb: 0.5 }}>CLASS</Typography>
                <Autocomplete
                  size="small"
                  options={filterClassrooms}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={filterClassroom}
                  onChange={(_, value) => { setFilterClassroom(value); setPage(0); }}
                  onInputChange={(_, value, reason) => { if (reason === "input") setFilterClassroomSearch(value); }}
                  filterOptions={(x) => x}
                  renderInput={(params) => <StyledTextField {...params} placeholder="All classes" />}
                />
              </Box>

              <Box sx={{ minWidth: 130 }}>
                <Typography variant="formSubHeading" sx={{ mb: 0.5 }}>STATUS</Typography>
                <FormControl size="small" fullWidth>
                  <StyledSelect
                    value={filterStatus}
                    onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
                    displayEmpty
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="partial">Partially Paid</MenuItem>
                    <MenuItem value="unpaid">Unpaid</MenuItem>
                  </StyledSelect>
                </FormControl>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={filterEvidence}
                    onChange={(e) => { setFilterEvidence(e.target.checked); setPage(0); }}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Has Attachments</Typography>}
              />
            </Box>
            {invoicesFetching && <LinearProgress />}
            {isMobile ? (
              <Box sx={{ px: 2, pb: 1 }}>
                {schoolInvoices?.map((item) => {
                  const totalAmount = item.total_amount ?? item.items?.reduce((sum, i) => sum + (i.amount || 0), 0) ?? 0;
                  const isPaid = item.paid;
                  const isPartial = item.partial_paid && !item.paid;
                  const statusLabel = isPaid ? "Paid" : isPartial ? "Partial" : "Unpaid";
                  const statusColor = isPaid ? "success" : isPartial ? "warning" : "error";
                  const hasDiscount = Boolean(item.invoice_discount || item.discount_amount > 0);
                  const isChecked = selectedInvoiceIds.includes(item.id);
                  return (
                    <Box key={item.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, mb: 1.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>Select</Typography>
                        <Checkbox
                          size="small"
                          checked={isChecked}
                          onChange={() => toggleInvoiceSelection(item.id)}
                        />
                      </Box>
                      <Typography sx={{ fontWeight: 600 }}>
                        {`${item.student?.first_name || ""} ${item.student?.last_name || ""}`.trim() || "—"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Class: {item.classroom?.name || "—"}</Typography>
                      <Typography variant="body2" color="text.secondary">Term: {item.term?.name || "—"}</Typography>
                      <Typography variant="body2" color="text.secondary">Session: {item.session?.name || "—"}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>Amount: ₦{totalAmount.toLocaleString()}</Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap" }}>
                        <Chip label={statusLabel} color={statusColor} size="small" />
                        {hasDiscount && <Chip label="Discount" color="info" size="small" variant="outlined" />}
                        <Chip
                          label={item.notified ? "Notified" : "Not notified"}
                          color={item.notified ? "default" : "warning"}
                          size="small"
                          variant={item.notified ? "outlined" : "filled"}
                        />
                      </Stack>
                      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                        <Box
                          className="tableCellLink"
                          onClick={() => {
                            setSelectedInvoice(item);
                            setOpenInvoice(true);
                          }}
                        >
                          View
                        </Box>
                        <Box
                          className="tableCellLink"
                          sx={{ color: "error.main" }}
                          onClick={() => handleDelete(item.id)}
                        >
                          Delete
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ borderBottom: "2px solid lightgrey" }}>
                      <StyledTableCell padding="checkbox">
                        <Checkbox
                          checked={allVisibleSelected}
                          indeterminate={selectedInvoiceIds.length > 0 && !allVisibleSelected}
                          onChange={(e) => handleToggleAll(e.target.checked)}
                        />
                      </StyledTableCell>
                      <StyledTableCell>STUDENT</StyledTableCell>
                      <StyledTableCell>CLASS</StyledTableCell>
                      <StyledTableCell>TERM</StyledTableCell>
                      <StyledTableCell>SESSION</StyledTableCell>
                      <StyledTableCell>AMOUNT</StyledTableCell>
                      <StyledTableCell>STATUS</StyledTableCell>
                      <StyledTableCell>VIEW</StyledTableCell>
                      <StyledTableCell>DELETE</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schoolInvoices?.map((item) => {
                      const totalAmount = item.total_amount ?? item.items?.reduce((sum, i) => sum + (i.amount || 0), 0) ?? 0;
                      const isPaid = item.paid;
                      const isPartial = item.partial_paid && !item.paid;
                      const statusLabel = isPaid ? "Paid" : isPartial ? "Partial" : "Unpaid";
                      const statusColor = isPaid ? "success" : isPartial ? "warning" : "error";
                      const hasDiscount = Boolean(item.invoice_discount || item.discount_amount > 0);
                      const isChecked = selectedInvoiceIds.includes(item.id);
                      return (
                        <StyledTableRow key={item.id}>
                          <StyledTableCell padding="checkbox">
                            <Checkbox
                              checked={isChecked}
                              onChange={() => toggleInvoiceSelection(item.id)}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            {`${item.student?.first_name || ""} ${item.student?.last_name || ""}`.trim() || "—"}
                          </StyledTableCell>
                          <StyledTableCell>{item.classroom?.name || "—"}</StyledTableCell>
                          <StyledTableCell>{item.term?.name || "—"}</StyledTableCell>
                          <StyledTableCell>{item.session?.name || "—"}</StyledTableCell>
                          <StyledTableCell>₦{totalAmount.toLocaleString()}</StyledTableCell>
                          <StyledTableCell>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              <Chip label={statusLabel} color={statusColor} size="small" />
                              {hasDiscount && <Chip label="Discount" color="info" size="small" variant="outlined" />}
                        <Chip
                          label={item.notified ? "Notified" : "Not notified"}
                          color={item.notified ? "default" : "warning"}
                          size="small"
                          variant={item.notified ? "outlined" : "filled"}
                        />
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box
                              className="tableCellLink"
                              onClick={() => {
                                setSelectedInvoice(item);
                                setOpenInvoice(true);
                              }}
                            >
                              View
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box
                              className="tableCellLink"
                              sx={{ color: "error.main" }}
                              onClick={() => handleDelete(item.id)}
                            >
                              Delete
                            </Box>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <TablePagination
              component="div"
              count={totalInvoices}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              sx={{ '.MuiTablePagination-toolbar': { flexWrap: 'wrap', minWidth: 0, px: 0 }, '.MuiTablePagination-spacer': { display: 'none' } }}
            />

            {selectedInvoiceIds.length > 0 && (
              <Box sx={{ px: "15px", pt: 1, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <StyledLoadingButton
                  variant="outlined"
                  loading={notifyLoading}
                  sx={{ textTransform: "none" }}
                  onClick={() => handleNotifyInvoices(selectedInvoiceIds)}
                >
                  Notify Selected ({selectedInvoiceIds.length})
                </StyledLoadingButton>
                <StyledLoadingButton
                  color="error"
                  loading={bulkDeleteLoading}
                  sx={{ textTransform: "none" }}
                  onClick={handleDeleteSelected}
                >
                  Delete Selected ({selectedInvoiceIds.length})
                </StyledLoadingButton>
              </Box>
            )}
          </StyledPaper>
        </Grid>
      </Grid>
    </div>
  );
}

export default Invoices;
